import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  Trophy, Droplets, Award, Beef, CandyOff, Moon, Dumbbell, Salad, Footprints, ClipboardList, 
  Star, Calendar, Target, Drumstick, Medal, Heart, Zap, Brain, Flame, Ribbon
} from 'lucide-react';

// Icon map for badges - expanded
const badgeIconMap = {
  'trophy': Trophy,
  'droplets': Droplets,
  'award': Award,
  'beef': Beef,
  'drumstick': Drumstick,
  'candy-off': CandyOff,
  'moon': Moon,
  'dumbbell': Dumbbell,
  'salad': Salad,
  'footprints': Footprints,
  'clipboard-list': ClipboardList,
  'star': Star,
  'calendar': Calendar,
  'target': Target,
  'medal': Medal,
  'heart': Heart,
  'zap': Zap,
  'brain': Brain,
  'flame': Flame,
  'ribbon': Ribbon,
};

const BadgeDetailsModal = ({ badge, isOpen, onClose }) => {
  if (!badge) return null;
  
  const BadgeIcon = badgeIconMap[badge.badge_icon] || Trophy;
  
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Get celebration message from either top level or milestone_info
  const celebrationMessage = badge.celebration_message || badge.milestone_info?.celebration_message;
  const healthBenefit = badge.health_benefit || badge.milestone_info?.health_benefit;
  const milestoneInfo = badge.milestone_info;
  
  // Determine milestone type context for display
  const getMilestoneDescription = () => {
    if (!milestoneInfo) return null;
    
    const { streak_days, action_name, action_id, points_threshold, milestone_type } = milestoneInfo;
    
    // Handle different milestone types
    if (milestone_type === 'perfect_day') {
      return 'Completed all 10 actions in one day!';
    }
    if (milestone_type === 'perfect_month') {
      return 'Achieved all 280 points in a month!';
    }
    if (milestone_type === 'monthly_points' && points_threshold) {
      return `Reached ${points_threshold} points in a month`;
    }
    if (milestone_type === 'checkin_streak' && streak_days) {
      return `${streak_days}-day check-in streak`;
    }
    // Handle "Any Action" milestones (action_streak with action_id='any')
    if (milestone_type === 'action_streak' && action_id === 'any' && streak_days) {
      return `${streak_days}-day check-in streak (any action)`;
    }
    if (milestone_type === 'action_streak' && streak_days && action_name && action_name !== 'Unknown Action') {
      return `${streak_days}-day streak • ${action_name}`;
    }
    // Fallback for older badges or action_streak without milestone_type
    if (streak_days && action_name && action_name !== 'Unknown Action') {
      return `${streak_days}-day streak • ${action_name}`;
    }
    
    return null;
  };

  const milestoneDescription = getMilestoneDescription();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white border border-stone-200 text-stone-900 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-xl font-heading text-stone-900">
            Badge Details
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center py-6">
          {/* Large Badge Icon */}
          <div className="w-24 h-24 bg-gradient-to-br from-[#EFB342] to-[#D99F30] hexagon flex items-center justify-center mb-6 shadow-lg">
            <BadgeIcon size={48} className="text-black" />
          </div>

          {/* Badge Name */}
          <h3 className="font-heading text-2xl font-bold text-stone-900 mb-2 text-center">
            {badge.badge_name}
          </h3>

          {/* Milestone Description */}
          {milestoneDescription && (
            <p className="text-stone-600 text-sm mb-4 text-center">
              {milestoneDescription}
            </p>
          )}

          {/* Celebration Message */}
          {celebrationMessage && (
            <div className="bg-[#F0EBE1] border border-stone-200 rounded-xl p-4 mb-4 w-full">
              <p className="text-stone-900 text-center font-semibold">
                🎉 {celebrationMessage}
              </p>
            </div>
          )}

          {/* Health Benefit */}
          {healthBenefit && (
            <div className="bg-[#EFB342]/10 border border-[#EFB342]/40 rounded-xl p-4 w-full">
              <p className="text-stone-700 text-sm text-center leading-relaxed">
                {healthBenefit}
              </p>
            </div>
          )}

          {/* Earned Date */}
          <p className="text-stone-500 text-xs mt-4">
            Earned on {formatDate(badge.earned_at)}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BadgeDetailsModal;
